import { ComponentRef, Directive, ElementRef, HostListener, Input, NgZone, OnDestroy, ViewContainerRef } from "@angular/core";

import { UserService } from "@mypxplat/xplat/core";

import { UserHoverCardComponent } from "../components/user-hover-card/user-hover-card.component";

/**
 * Open delay (DENG-846). Long enough that sweeping the pointer across a column of avatars
 * never flashes a card, short enough that a deliberate hover feels immediate.
 */
const OPEN_DELAY_MS = 300;

/**
 * Close grace period. The card is parented to <body> and sits 8px off the avatar, so the
 * pointer necessarily leaves the anchor before it reaches the card — this is the window it
 * has to get there. The card cancels the timer on its own mouseenter.
 */
const CLOSE_DELAY_MS = 200;

/**
 * Shows a Slack-style profile card when the pointer rests on a community member's avatar
 * (DENG-846).
 *
 *   <a [mypUserHoverCard]="post.author.id"> … avatar … </a>
 *
 * Attach it to the element that WRAPS the avatar (the existing profile link), not the
 * <img> — the link is the thing with a hit area even when the photo fails to load.
 *
 * One card exists at a time, application-wide: `_active` is static, so hovering a second
 * avatar tears down the first card rather than stacking. The card component itself is
 * created through this directive's `ViewContainerRef`, which means it participates in
 * change detection and is destroyed automatically if the post that owns this directive is
 * removed from the DOM mid-hover (feed re-renders constantly). Its element is then moved
 * to <body> so no ancestor's `overflow` or stacking context can clip it.
 *
 * Pointer-only by design: it is suppressed on touch/coarse pointers, where a hover is
 * really a tap heading for the profile page. See the component for why it is aria-hidden.
 */
@Directive({
  selector: "[mypUserHoverCard]",
  standalone: false,
})
export class UserHoverCardDirective implements OnDestroy {
  /** Whose card to show. Falsy disables the directive entirely (guests, ghost authors). */
  @Input("mypUserHoverCard") userId: string;

  /** Opt out per-site without unbinding — e.g. the current user's own avatar. */
  @Input() hoverCardDisabled = false;

  /** The one card on screen, across every instance of this directive. */
  private static _active: { directive: UserHoverCardDirective; ref: ComponentRef<UserHoverCardComponent> } | null = null;

  private _openTimer: ReturnType<typeof setTimeout> | null = null;
  private _closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private _el: ElementRef<HTMLElement>,
    private _vcr: ViewContainerRef,
    private _zone: NgZone,
    private _userService: UserService
  ) {}

  ngOnDestroy() {
    this._clearTimers();
    this._closeIfMine();
    this._unbindDismissListeners();
  }

  @HostListener("mouseenter")
  onMouseEnter() {
    if (!this._enabled()) return;
    this._clearCloseTimer();
    // Already showing this exact person (pointer moved avatar → card → avatar): keep it.
    if (UserHoverCardDirective._active?.directive === this) return;
    this._clearOpenTimer();
    this._openTimer = setTimeout(() => {
      this._openTimer = null;
      this._open();
    }, OPEN_DELAY_MS);
  }

  @HostListener("mouseleave")
  onMouseLeave() {
    this._clearOpenTimer();
    this._scheduleClose();
  }

  /**
   * A click is a navigation to the profile — the card would otherwise linger over the page
   * it just left, since the anchor is removed from under the pointer by the route change.
   */
  @HostListener("click")
  onClick() {
    this._clearTimers();
    this._closeIfMine();
  }

  private _enabled(): boolean {
    if (this.hoverCardDisabled || !this.userId) return false;
    // Signed-out visitors can browse the community, but `user_profiles/{id}` is closed to
    // them by Firestore rules — every card would read "Profile unavailable". Show nothing
    // rather than a card that can only disappoint; the avatar still links to the profile,
    // which prompts signup on its own.
    if (!this._userService.user?.id) return false;
    // Coarse pointers synthesize mouseenter on tap. Same guard the reaction palette uses.
    if (typeof window !== "undefined" && window.matchMedia && !window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      return false;
    }
    return true;
  }

  private _open() {
    if (!this._enabled()) return;
    UserHoverCardDirective._closeActive();

    const ref = this._vcr.createComponent(UserHoverCardComponent);
    ref.setInput("anchor", this._el.nativeElement);
    ref.setInput("userId", this.userId);

    // Out of the post's stacking/overflow context and into the page's top layer.
    document.body.appendChild(ref.location.nativeElement);

    const instance = ref.instance;
    instance.keepOpen.subscribe(() => this._clearCloseTimer());
    instance.requestClose.subscribe(() => {
      this._clearTimers();
      this._closeIfMine();
    });

    UserHoverCardDirective._active = { directive: this, ref };
    this._bindDismissListeners();

    // Measure once the first paint of the card exists; the component measures again when
    // its profile data lands and the height changes.
    ref.changeDetectorRef.detectChanges();
    instance.reposition();
  }

  private _scheduleClose() {
    this._clearCloseTimer();
    this._closeTimer = setTimeout(() => {
      this._closeTimer = null;
      this._closeIfMine();
    }, CLOSE_DELAY_MS);
  }

  private _closeIfMine() {
    if (UserHoverCardDirective._active?.directive !== this) return;
    UserHoverCardDirective._closeActive();
    this._unbindDismissListeners();
  }

  private static _closeActive() {
    const active = UserHoverCardDirective._active;
    if (!active) return;
    UserHoverCardDirective._active = null;
    const host = active.ref.location.nativeElement as HTMLElement;
    // Moved out of the view's DOM range on open, so `destroy()` alone won't unmount it.
    host.parentNode?.removeChild(host);
    // When the owning view is torn down (the feed re-renders constantly), Angular may have
    // destroyed this component already before running the directive's ngOnDestroy. The DOM
    // node still needs removing above; the destroy itself is then a no-op at best.
    if (!active.ref.hostView.destroyed) active.ref.destroy();
  }

  // --- dismiss-on-context-change ----------------------------------------------------
  // The card is position:fixed against a viewport-relative rect. Rather than recompute on
  // every scroll frame, close it — the pointer has left the avatar by then anyway.

  private _onScrollOrResize = () => {
    this._clearTimers();
    this._closeIfMine();
  };

  private _onKeydown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    this._clearTimers();
    this._closeIfMine();
  };

  private _bindDismissListeners() {
    // Outside Angular: these fire at scroll frequency and none of them touch bindings —
    // the close path runs `destroy()`, which does its own teardown.
    this._zone.runOutsideAngular(() => {
      window.addEventListener("scroll", this._onScrollOrResize, true);
      window.addEventListener("resize", this._onScrollOrResize);
      document.addEventListener("keydown", this._onKeydown, true);
    });
  }

  private _unbindDismissListeners() {
    window.removeEventListener("scroll", this._onScrollOrResize, true);
    window.removeEventListener("resize", this._onScrollOrResize);
    document.removeEventListener("keydown", this._onKeydown, true);
  }

  private _clearTimers() {
    this._clearOpenTimer();
    this._clearCloseTimer();
  }

  private _clearOpenTimer() {
    if (this._openTimer) {
      clearTimeout(this._openTimer);
      this._openTimer = null;
    }
  }

  private _clearCloseTimer() {
    if (this._closeTimer) {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }
  }
}
